import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, signal } from "@angular/core";
import { ColDef } from "ag-grid-community";

// EZ
import { EzBaseComponent } from "@template-ui/components";
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import {
  ButtonClickEvent,
  CrudMatchType,
  EzFormInputType,
  EzGridContextMenuBuiltInItem,
  EzGridContextMenuBuiltInItemId,
  EzGridContextMenuContext,
  EzGridContextMenuConfigFactory,
  FormValuesObject,
  InputConfig,
  InputConfigForFilters,
  PanelHeaderConfig,
} from "@template-ui/types";

type ContextMenuCustomItemScope = "cell" | "header" | "both";
type ContextMenuPresetId = "fullFeatured" | "clipboardOnly" | "headerColumnTools" | "customItemDemo" | "custom";

type ContextMenuSampleFormValues = FormValuesObject & {
  presetId: ContextMenuPresetId;
  enableMenu: boolean;
  includeClipboardGroup: boolean;
  includeFilterGroup: boolean;
  includeSortGroup: boolean;
  includeColumnGroup: boolean;
  includeCustomItem: boolean;
  enableColumnResizing: boolean;
  enableColumnReordering: boolean;
  disableActorIdColumn: boolean;
  customItemScope: ContextMenuCustomItemScope;
  customItemLabelTemplate: string;
  customItemMessageTemplate: string;
};

type ContextMenuPresetDefinition = {
  label: string;
  description: string;
  formValues: ContextMenuSampleFormValues;
};

type MenuFormFieldName =
  | "presetId"
  | "enableMenu"
  | "includeClipboardGroup"
  | "includeFilterGroup"
  | "includeSortGroup"
  | "includeColumnGroup"
  | "includeCustomItem"
  | "disableActorIdColumn"
  | "customItemScope"
  | "customItemLabelTemplate"
  | "customItemMessageTemplate";

type ColumnFormFieldName =
  | "enableColumnResizing"
  | "enableColumnReordering";

type StarWarsActor = {
  id: string;
  firstName: string;
  lastName: string;
  actorId: string;
  dateOfBirth: string;
  notableRole: string;
  trilogy: string;
  isLiving: boolean;
};

const DEFAULT_FORM_VALUES: ContextMenuSampleFormValues = {
  presetId: "fullFeatured",
  enableMenu: true,
  includeClipboardGroup: true,
  includeFilterGroup: true,
  includeSortGroup: true,
  includeColumnGroup: true,
  includeCustomItem: true,
  enableColumnResizing: true,
  enableColumnReordering: true,
  disableActorIdColumn: true,
  customItemScope: "cell",
  customItemLabelTemplate: "Sample Custom Item for {{rowData.firstName}} {{rowData.lastName}}",
  customItemMessageTemplate: "Custom item clicked for {{rowData.firstName}} {{rowData.lastName}} on {{columnId}}.",
};

const CONTEXT_MENU_PRESETS: Record<Exclude<ContextMenuPresetId, "custom">, ContextMenuPresetDefinition> = {
  fullFeatured: {
    label: "Full Featured",
    description: "Full menu with clipboard, filter, sort, column actions, and one custom item.",
    formValues: { ...DEFAULT_FORM_VALUES, presetId: "fullFeatured" },
  },
  clipboardOnly: {
    label: "Clipboard Only",
    description: "Cell-focused clipboard actions without filter, sort, or column-management items.",
    formValues: {
      ...DEFAULT_FORM_VALUES,
      presetId: "clipboardOnly",
      includeFilterGroup: false,
      includeSortGroup: false,
      includeColumnGroup: false,
      includeCustomItem: false,
      disableActorIdColumn: false,
    },
  },
  headerColumnTools: {
    label: "Header Column Tools",
    description: "Header-oriented sort and column-management actions for layout-heavy grids.",
    formValues: {
      ...DEFAULT_FORM_VALUES,
      presetId: "headerColumnTools",
      includeClipboardGroup: false,
      includeFilterGroup: false,
      includeCustomItem: false,
      enableColumnResizing: true,
      enableColumnReordering: true,
      disableActorIdColumn: false,
    },
  },
  customItemDemo: {
    label: "Custom Item Demo",
    description: "Minimal built-ins plus one custom action with header-and-cell visibility.",
    formValues: {
      ...DEFAULT_FORM_VALUES,
      presetId: "customItemDemo",
      includeClipboardGroup: false,
      includeSortGroup: false,
      includeColumnGroup: false,
      customItemScope: "both",
    },
  },
};

const MENU_FORM_FIELD_NAMES: readonly MenuFormFieldName[] = [
  "presetId",
  "enableMenu",
  "includeClipboardGroup",
  "includeFilterGroup",
  "includeSortGroup",
  "includeColumnGroup",
  "includeCustomItem",
  "disableActorIdColumn",
  "customItemScope",
  "customItemLabelTemplate",
  "customItemMessageTemplate",
];

const COLUMN_FORM_FIELD_NAMES: readonly ColumnFormFieldName[] = [
  "enableColumnResizing",
  "enableColumnReordering",
];

@DocMeta({
  hide: true,
  category: "Components",
  showcaseTitle: "Context Menu",
  showcaseDesc: `## EZ Grid Context Menu

Use the controls below to toggle built-in menu groups, suppress the menu on one column, and configure a custom menu item.

- Built-in groups map to the EZ Grid context-menu design lanes.
- The custom item uses label and message templates so you can try row and column tokens.
- Click **Apply Menu Settings** after changing the controls to rebuild the grid with the updated menu configuration.
`,
  showcaseAttachTo: [{ libName: "Base Library", classNames: ["EzGridComponent"] }],
})
@Component({
  selector: "ui-grid-context-menu-sample, ez-grid-context-menu-sample",
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./ez-grid-context-menu-sample.component.html",
  styleUrl: "./ez-grid-context-menu-sample.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EzGridContextMenuSampleComponent extends EzBaseComponent {
  // ----------------------------------------------------------------
  //  Public Template-Bound Properties
  // ----------------------------------------------------------------

  /** Lets the sample remount the grid after menu-configuration changes. */
  protected readonly showGrid = signal<boolean>(false);

  /** Lets the sample remount the control forms after preset-driven value reseeding. */
  protected readonly showControlForms = signal<boolean>(true);

  /** Displays the last built-in or custom action triggered by the sample. */
  protected readonly statusText = signal<string>("Right-click a cell or header to open the EZ Grid context menu.");

  /** Short description of the selected preset or current custom configuration. */
  protected readonly presetDescription = signal<string>(CONTEXT_MENU_PRESETS.fullFeatured.description);

  /** Copyable code preview for the selected preset or current custom settings. */
  protected readonly contextMenuConfigPreview = signal<string>("");

  /** Two-way bound values for the menu-settings form. */
  protected menuControlFormValues: FormValuesObject = this.pickFormValues(DEFAULT_FORM_VALUES, MENU_FORM_FIELD_NAMES);

  /** Two-way bound values for the column-properties form. */
  protected columnControlFormValues: FormValuesObject = this.pickFormValues(DEFAULT_FORM_VALUES, COLUMN_FORM_FIELD_NAMES);

  /** EZ Form config for the menu-specific controls. */
  protected readonly menuControlFormConfig: InputConfig[] = [
    {
      name: "menuSectionHeading",
      type: EzFormInputType.HTML,
      label: "<div class='sample-form-section-heading'>Menu Settings</div>",
      width: "4x",
      row: 1,
    },

    // Row 2
    {
      name: "presetId",
      label: "Preset",
      type: EzFormInputType.SELECT,
      width: "2x",
      row: 2,
      options: [
        { value: "fullFeatured", viewValue: "Full Featured" },
        { value: "clipboardOnly", viewValue: "Clipboard Only" },
        { value: "headerColumnTools", viewValue: "Header Column Tools" },
        { value: "customItemDemo", viewValue: "Custom Item Demo" },
        { value: "custom", viewValue: "Custom / Modified" },
      ],
    },

    // Row 3
    { name: "enableMenu", label: "Enable Menu", type: EzFormInputType.CHECKBOX, width: "1x", row: 3 },
    { name: "includeClipboardGroup", label: "Clipboard Items", type: EzFormInputType.CHECKBOX, width: "1x", row: 3 },
    { name: "includeFilterGroup", label: "Filter Items", type: EzFormInputType.CHECKBOX, width: "1x", row: 3 },
    { name: "includeSortGroup", label: "Sort Items", type: EzFormInputType.CHECKBOX, width: "1x", row: 3 },

    // Row 4
    { name: "includeColumnGroup", label: "Column Items", type: EzFormInputType.CHECKBOX, width: "1x", row: 4 },
    { name: "includeCustomItem", label: "Custom Item", type: EzFormInputType.CHECKBOX, width: "1x", row: 4 },
    { name: "disableActorIdColumn", label: "Disable Actor Id Menu", type: EzFormInputType.CHECKBOX, width: "1x", row: 4 },

    // Row 5
    {
      name: "customItemScope",
      label: "Custom Item Scope",
      type: EzFormInputType.SELECT,
      width: "1x",
      row: 4,
      options: [
        { value: "cell", viewValue: "Cell Only" },
        { value: "header", viewValue: "Header Only" },
        { value: "both", viewValue: "Cell + Header" },
      ],
    },

    // Row 6
    {
      name: "customItemLabelTemplate",
      label: "Custom Item Label Template",
      type: EzFormInputType.TEXT,
      width: "4x",
      row: 6,
    },

    // Row 7
    {
      name: "customItemMessageTemplate",
      label: "Custom Item Message Template",
      type: EzFormInputType.TEXT,
      width: "4x",
      row: 7,
    },
  ];

  /** EZ Form config for the column-property toggles and sample actions. */
  protected readonly columnControlFormConfig: InputConfig[] = [
    {
      name: "columnSectionHeading",
      type: EzFormInputType.HTML,
      label: "<div class='sample-form-section-heading'>Column Properties</div>",
      width: "1x",
      row: 1,
    },

    // Row 2
    { name: "enableColumnResizing", label: "Enable Column Resizing", type: EzFormInputType.CHECKBOX, width: "1x", row: 2 },
    { name: "enableColumnReordering", label: "Enable Column Reordering", type: EzFormInputType.CHECKBOX, width: "1x", row: 3 },

    // Row 3
    {
      name: "applyMenuSettings",
      buttonText: "Apply Menu Settings",
      type: EzFormInputType.BUTTON,
      width: "1x",
      row: 4,
    },
    { name: "resetMenuSettings", buttonText: "Reset Sample", type: EzFormInputType.BUTTON, width: "2x", row: 5 },
  ];

  /** Header filter inputs used by `Filter To` and `Clear Filter`. */
  protected readonly filterInputConfigs: InputConfigForFilters[] = [
    {
      type: EzFormInputType.TEXT,
      name: "lastName",
      label: "Last Name",
      width: "1x",
      matchType: CrudMatchType.Contains,
    },
    {
      type: EzFormInputType.SELECT,
      name: "trilogy",
      label: "Trilogy",
      width: "1x",
      matchType: CrudMatchType.Equals,
      options: [
        { value: "original", viewValue: "Original" },
        { value: "prequel", viewValue: "Prequel" },
        { value: "sequel", viewValue: "Sequel" },
        { value: "expanded", viewValue: "Expanded" },
      ],
    },
  ];

  /** Panel-header config shared by the sample grid. */
  protected readonly panelHeaderConfig: PanelHeaderConfig = {
    panelHeaderTitle: "Context Menu Demo Grid",
    subtitle: "Use Apply Menu Settings after changing the controls above.",
    showStandardFilterControls: true,
    showReloadButton: true,
    hideExportToExcel: true,
    settingsKey: "Showcase:EzGridContextMenuSample",
    filterInputConfigs: this.filterInputConfigs,
  };

  /** Current grid-level menu configuration generated from the sample controls. */
  protected contextMenuConfig: EzGridContextMenuConfigFactory<StarWarsActor> = () => ({
    enabled: true,
    builtInItems: [],
  });

  /** Column definitions rebuilt alongside the sample menu settings. */
  protected colDefs: ColDef<StarWarsActor>[] = [];

  // ----------------------------------------------------------------
  //  Lifecycle Hooks
  // ----------------------------------------------------------------

  public constructor() {
    super();
    effect(() => {
      this.contextMenuConfigPreview(); // track signal
      setTimeout(() => this.Prism.highlightAll(), 0);
    });
  }

  public ngOnInit(): void {
    this.rebuildSampleConfiguration();
  }

  // ----------------------------------------------------------------
  //  Public Methods
  // ----------------------------------------------------------------

  /**
   * Tracks menu-form edits using delta (changed fields only) to avoid the full-snapshot
   * ambiguity of formValuesChange, where every emission includes the current presetId value
   * and falsely triggers the preset-selection branch on checkbox clicks.
   */
  protected onMenuControlFormValuesChange(delta: Partial<FormValuesObject>): void {
    const presetId = delta["presetId"];
    if (typeof presetId === "string" && presetId !== "custom" && this.isNamedPresetId(presetId)) {
      const presetFormValues = CONTEXT_MENU_PRESETS[presetId].formValues;
      this.applyPresetFormValues(presetFormValues, false);
      this.refreshPresetPreview(presetFormValues);
      return;
    }

    // A non-preset field changed — merge the delta and mark as custom.
    // Because we use [formValues] (one-way), ez-form receives the updated presetId
    // and flips the dropdown to 'Custom / Modified' via its reactive setter.
    this.menuControlFormValues = {
      ...this.menuControlFormValues,
      ...delta,
      presetId: "custom",
    };
    this.refreshPresetPreview(this.getCurrentSettings());
  }

  /** Tracks column-form edits without rebuilding the grid until Apply is clicked. */
  protected onColumnControlFormValuesChange(formValues: Partial<FormValuesObject>): void {
    this.columnControlFormValues = {
      ...this.columnControlFormValues,
      ...formValues,
    };
    this.menuControlFormValues = {
      ...this.menuControlFormValues,
      presetId: "custom",
    };
    this.refreshPresetPreview(this.getCurrentSettings());
  }

  /** Applies or resets the sample controls. */
  protected onControlFormButtonClick(event: ButtonClickEvent): void {
    const buttonName = event.name;

    switch (buttonName) {
      case "applyMenuSettings":
        this.rebuildSampleConfiguration();
        break;
      case "resetMenuSettings":
        this.applyPresetFormValues(DEFAULT_FORM_VALUES, true);
        this.rebuildSampleConfiguration();
        break;
    }
  }

  /** Copies the current config preview to the clipboard. */
  protected onCopyConfigClick(): void {
    const preview = this.contextMenuConfigPreview();
    if (!preview) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(preview);
      this.statusText.set("Copied the current context-menu config preview.");
      return;
    }

    this.statusText.set("Clipboard copy is not available in this browser context.");
  }

  // ----------------------------------------------------------------
  //  Private Helper Methods
  // ----------------------------------------------------------------

  /** Rebuilds the grid menu config and remounts the sample grid. */
  private rebuildSampleConfiguration(): void {
    const settings = this.getCurrentSettings();

    this.contextMenuConfig = this.buildContextMenuConfig(settings);
    this.colDefs = this.buildColumnDefs(settings);
    this.applyPresetFormValues(settings, true);
    this.refreshPresetPreview(settings);
    const builtIns = this.buildBuiltInItems(settings);
    const menuEnabled = settings.enableMenu || builtIns.length > 0 || settings.includeCustomItem;
    this.statusText.set(
      `Menu enabled: ${menuEnabled ? "yes" : "no"}. Built-ins: ${builtIns.join(", ") || "none"}. ` +
      `Resizing: ${settings.enableColumnResizing ? "on" : "off"}. Reordering: ${settings.enableColumnReordering ? "on" : "off"}.`
    );

    // EZ Grid resolves context-menu config during setup, so the sample remounts the grid after Apply
    // instead of layering on extra runtime reconfiguration logic.
    this.showGrid.set(false);
    setTimeout(() => this.showGrid.set(true), 0);
  }

  /** Seeds both form slices from one complete settings object. */
  private applyPresetFormValues(settings: ContextMenuSampleFormValues, remountForms: boolean): void {
    this.menuControlFormValues = this.pickFormValues(settings, MENU_FORM_FIELD_NAMES);
    this.columnControlFormValues = this.pickFormValues(settings, COLUMN_FORM_FIELD_NAMES);

    if (remountForms) {
      this.remountControlForms();
    }
  }

  /** Combines the independent menu and column form slices into the sample's full settings model. */
  private getCurrentSettings(): ContextMenuSampleFormValues {
    return this.normalizeFormValues({
      ...DEFAULT_FORM_VALUES,
      ...(this.menuControlFormValues as Partial<ContextMenuSampleFormValues>),
      ...(this.columnControlFormValues as Partial<ContextMenuSampleFormValues>),
    } as ContextMenuSampleFormValues);
  }

  /** Picks only the fields relevant to one ez-form instance. */
  private pickFormValues<TKey extends keyof ContextMenuSampleFormValues>(
    settings: ContextMenuSampleFormValues,
    fieldNames: readonly TKey[],
  ): FormValuesObject {
    return fieldNames.reduce<FormValuesObject>((values, fieldName) => {
      values[fieldName] = settings[fieldName];
      return values;
    }, {});
  }

  /** Recreates the control forms so ez-form reloads the latest seeded values. */
  private remountControlForms(): void {
    this.showControlForms.set(false);
    setTimeout(() => this.showControlForms.set(true), 0);
  }

  /** Converts raw form values into the stricter settings shape used by the sample. */
  private normalizeFormValues(values: ContextMenuSampleFormValues): ContextMenuSampleFormValues {
    return {
      enableMenu: !!values.enableMenu,
      includeClipboardGroup: !!values.includeClipboardGroup,
      includeFilterGroup: !!values.includeFilterGroup,
      includeSortGroup: !!values.includeSortGroup,
      includeColumnGroup: !!values.includeColumnGroup,
      includeCustomItem: !!values.includeCustomItem,
      enableColumnResizing: !!values.enableColumnResizing,
      enableColumnReordering: !!values.enableColumnReordering,
      disableActorIdColumn: !!values.disableActorIdColumn,
      presetId: this.isPresetId(values.presetId) ? values.presetId : "custom",
      customItemScope: (values.customItemScope as ContextMenuCustomItemScope) || "cell",
      customItemLabelTemplate: String(values.customItemLabelTemplate || DEFAULT_FORM_VALUES.customItemLabelTemplate),
      customItemMessageTemplate: String(values.customItemMessageTemplate || DEFAULT_FORM_VALUES.customItemMessageTemplate),
    };
  }

  /** Builds the active built-in item list from the group toggles. */
  private buildBuiltInItems(settings: ContextMenuSampleFormValues): EzGridContextMenuBuiltInItem[] {
    const builtIns: EzGridContextMenuBuiltInItem[] = [];

    if (settings.includeClipboardGroup) {
      builtIns.push(
        EzGridContextMenuBuiltInItemId.COPY,
        EzGridContextMenuBuiltInItemId.COPY_RAW,
        EzGridContextMenuBuiltInItemId.COPY_SELECTED_TEXT,
        EzGridContextMenuBuiltInItemId.PASTE,
      );
    }

    if (settings.includeFilterGroup) {
      builtIns.push(EzGridContextMenuBuiltInItemId.FILTER_TO, EzGridContextMenuBuiltInItemId.CLEAR_FILTER);
    }

    if (settings.includeSortGroup) {
      builtIns.push(EzGridContextMenuBuiltInItemId.SORT_ASC, EzGridContextMenuBuiltInItemId.SORT_DESC);
    }

    if (settings.includeColumnGroup) {
      builtIns.push(
        EzGridContextMenuBuiltInItemId.PIN_LEFT,
        EzGridContextMenuBuiltInItemId.PIN_RIGHT,
        EzGridContextMenuBuiltInItemId.UNPIN,
        EzGridContextMenuBuiltInItemId.CHOOSE_COLUMNS,
        EzGridContextMenuBuiltInItemId.RESET_COLUMNS,
        EzGridContextMenuBuiltInItemId.RESET_COLUMN,
      );
    }

    return builtIns;
  }

  /** Builds the grid-level context-menu config for the sample grid. */
  private buildContextMenuConfig(settings: ContextMenuSampleFormValues): EzGridContextMenuConfigFactory<StarWarsActor> {
    const builtInItems = this.buildBuiltInItems(settings);
    // Having any items configured implicitly enables the menu even if enableMenu is unchecked.
    const menuEnabled = settings.enableMenu || builtInItems.length > 0 || settings.includeCustomItem;

    return (currentMenuContext) => ({
      enabled: menuEnabled,
      builtInItems,
      customItems: settings.includeCustomItem
        ? [
            {
              id: "sampleCustomItem",
              labelTemplate: settings.customItemLabelTemplate,
              visible: computed(() => {
                const context = currentMenuContext();
                if (!context) {
                  return false;
                }

                switch (settings.customItemScope) {
                  case "cell":
                    return context.target === "cell";
                  case "header":
                    return context.target === "header";
                  default:
                    return true;
                }
              }),
              command: (context) => {
                const message = this.ezText.resolveTemplate(
                  settings.customItemMessageTemplate,
                  this.buildTemplateContext(context),
                );
                this.statusText.set(message);
              },
            },
          ]
        : [],
    });
  }

  /** Creates colDefs including an optional column-level menu suppression example. */
  private buildColumnDefs(settings: ContextMenuSampleFormValues): ColDef<StarWarsActor>[] {
    const sharedColDefSettings: Pick<ColDef<StarWarsActor>, "resizable" | "suppressMovable"> = {
      resizable: settings.enableColumnResizing,
      suppressMovable: !settings.enableColumnReordering,
    };

    return [
      { ...sharedColDefSettings, field: "id", headerName: "Id", maxWidth: 110 },
      { ...sharedColDefSettings, field: "firstName", headerName: "First Name" },
      { ...sharedColDefSettings, field: "lastName", headerName: "Last Name", filter: "agTextColumnFilter" },
      {
        ...sharedColDefSettings,
        field: "actorId",
        headerName: "Actor Id",
        maxWidth: 130,
        ezContextMenu: settings.disableActorIdColumn ? { enabled: false } : undefined,
      },
      { ...sharedColDefSettings, field: "dateOfBirth", headerName: "Date Of Birth", maxWidth: 150 },
      { ...sharedColDefSettings, field: "notableRole", headerName: "Notable Role", minWidth: 220, flex: 1 },
      { ...sharedColDefSettings, field: "trilogy", headerName: "Trilogy", maxWidth: 130, filter: "agTextColumnFilter" },
      { ...sharedColDefSettings, field: "isLiving", headerName: "Is Living", maxWidth: 120 },
    ];
  }

  /** Exposes row values as both top-level and dotted `rowData.*` tokens for the sample message template. */
  private buildTemplateContext(context: EzGridContextMenuContext<StarWarsActor>): Record<string, unknown> {
    const rowData = context.rowData;
    const templateContext: Record<string, unknown> = { ...context };

    if (!rowData || typeof rowData !== "object" || Array.isArray(rowData)) {
      return templateContext;
    }

    for (const [key, value] of Object.entries(rowData as Record<string, unknown>)) {
      if (typeof templateContext[key] === "undefined") {
        templateContext[key] = value;
      }

      templateContext[`rowData.${key}`] = value;
    }

    return templateContext;
  }

  /** Refreshes the preset description and copyable config preview. */
  private refreshPresetPreview(settings: ContextMenuSampleFormValues): void {
    if (settings.presetId !== "custom" && this.isPresetId(settings.presetId)) {
      this.presetDescription.set(CONTEXT_MENU_PRESETS[settings.presetId].description);
    } else {
      this.presetDescription.set("Custom configuration based on the current control values.");
    }

    this.contextMenuConfigPreview.set(this.buildContextMenuConfigPreview(settings));
  }

  /** Builds a copyable TypeScript snippet for the current menu settings. */
  private buildContextMenuConfigPreview(settings: ContextMenuSampleFormValues): string {
    const builtInItems = this.buildBuiltInItems(settings);
    const menuEnabled = settings.enableMenu || builtInItems.length > 0 || settings.includeCustomItem;
    const builtInItemsText = builtInItems.length
      ? `  builtInItems: [\n${builtInItems.map((item) => `    ${this.getBuiltInEnumReference(item)},`).join("\n")}\n  ],\n`
      : "";

    const customItemsText = settings.includeCustomItem
      ? `  customItems: [
    {
      id: "sampleCustomItem",
      labelTemplate: "${this.escapeTemplateString(settings.customItemLabelTemplate)}",${
        settings.customItemScope === "both"
          ? ""
          : `
      visible: computed(() => currentMenuContext()?.target === "${settings.customItemScope}"),`
      }
      command: (context) => {
        const message = ezText.resolveTemplate(
          "${this.escapeTemplateString(settings.customItemMessageTemplate)}",
          context,
        );
      },
    },
  ],\n`
      : "";

    const enumImportText = builtInItems.length
      ? `import { EzGridContextMenuBuiltInItemId, EzGridContextMenuConfigFactory } from "@template-ui/types";\n\n`
      : `import { EzGridContextMenuConfigFactory } from "@template-ui/types";\n\n`;

    const contextMenuConfigText = settings.includeCustomItem
      ? `${enumImportText}const contextMenuConfig: EzGridContextMenuConfigFactory<RowData> = (currentMenuContext) => ({
  enabled: ${menuEnabled},
${builtInItemsText}${customItemsText}});`
      : `${enumImportText}const contextMenuConfig: EzGridContextMenuConfigFactory<RowData> = () => ({
  enabled: ${menuEnabled},
${builtInItemsText}});`;

    const actorIdOverrideText = settings.disableActorIdColumn
      ? `\n\nconst actorIdColumn: ColDef<RowData> = {
  field: "actorId",
  ezContextMenu: { enabled: false },
};`
      : "";

    return `${contextMenuConfigText}${actorIdOverrideText}`;
  }

  /** Escapes menu-template text for the copyable TypeScript preview. */
  private escapeTemplateString(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  /** Converts a built-in id into the enum member reference shown in the sample preview. */
  private getBuiltInEnumReference(item: EzGridContextMenuBuiltInItem): string {
    switch (item) {
      case EzGridContextMenuBuiltInItemId.COPY:
        return "EzGridContextMenuBuiltInItemId.COPY";
      case EzGridContextMenuBuiltInItemId.COPY_RAW:
        return "EzGridContextMenuBuiltInItemId.COPY_RAW";
      case EzGridContextMenuBuiltInItemId.COPY_SELECTED_TEXT:
        return "EzGridContextMenuBuiltInItemId.COPY_SELECTED_TEXT";
      case EzGridContextMenuBuiltInItemId.PASTE:
        return "EzGridContextMenuBuiltInItemId.PASTE";
      case EzGridContextMenuBuiltInItemId.FILTER_TO:
        return "EzGridContextMenuBuiltInItemId.FILTER_TO";
      case EzGridContextMenuBuiltInItemId.CLEAR_FILTER:
        return "EzGridContextMenuBuiltInItemId.CLEAR_FILTER";
      case EzGridContextMenuBuiltInItemId.SORT_ASC:
        return "EzGridContextMenuBuiltInItemId.SORT_ASC";
      case EzGridContextMenuBuiltInItemId.SORT_DESC:
        return "EzGridContextMenuBuiltInItemId.SORT_DESC";
      case EzGridContextMenuBuiltInItemId.PIN_LEFT:
        return "EzGridContextMenuBuiltInItemId.PIN_LEFT";
      case EzGridContextMenuBuiltInItemId.PIN_RIGHT:
        return "EzGridContextMenuBuiltInItemId.PIN_RIGHT";
      case EzGridContextMenuBuiltInItemId.UNPIN:
        return "EzGridContextMenuBuiltInItemId.UNPIN";
      case EzGridContextMenuBuiltInItemId.CHOOSE_COLUMNS:
        return "EzGridContextMenuBuiltInItemId.CHOOSE_COLUMNS";
      case EzGridContextMenuBuiltInItemId.RESET_COLUMNS:
        return "EzGridContextMenuBuiltInItemId.RESET_COLUMNS";
      case EzGridContextMenuBuiltInItemId.RESET_COLUMN:
        return "EzGridContextMenuBuiltInItemId.RESET_COLUMN";
    }

    return `"${item}"`;
  }

  /** Narrows preset ids coming back from the form values object. */
  private isPresetId(value: unknown): value is ContextMenuPresetId {
    return value === "fullFeatured" ||
      value === "clipboardOnly" ||
      value === "headerColumnTools" ||
      value === "customItemDemo" ||
      value === "custom";
  }

  /** Narrows preset ids to the named sample presets, excluding the ad hoc custom mode. */
  private isNamedPresetId(value: unknown): value is Exclude<ContextMenuPresetId, "custom"> {
    return value === "fullFeatured" ||
      value === "clipboardOnly" ||
      value === "headerColumnTools" ||
      value === "customItemDemo";
  }
}
